import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { InvoiceItemsTypeEnum } from '../Configurations/invoicingConfig';

const InvoicingSelectionContext = createContext();

export const useInvoicingSelectionContext = () => {
    const context = useContext(InvoicingSelectionContext);
    if (!context) {
        throw new Error(
            'useInvoicingSelectionContext must be used within an InvoicingSelectionContextProvider'
        );
    }
    return context;
};

export const SelectionStatus = {
    SELECTED: 'selected',
    INDETERMINATE: 'indeterminate',
    NOT_SELECTED: false,
};

const initializeSelectableItems = (type, items) =>
    items.map((item) => ({
        type,
        id: item.id,
        status: SelectionStatus.NOT_SELECTED,
    }));
const initializeSelectableItemsWithSubItems = (type, items, childrenKey) =>
    items.map((item) => ({
        type,
        id: item.id,
        hasSubItems: true,
        status: SelectionStatus.NOT_SELECTED,
        subItems:
            item[childrenKey]?.map((child) => ({
                ...child,
                id: child.id,
                status: SelectionStatus.NOT_SELECTED,
            })) || [],
    }));
const initializeBooking = (booking) => {
    const timesheetItems = initializeSelectableItemsWithSubItems(
        InvoiceItemsTypeEnum.Timesheet,
        booking.timesheets || [],
        'lineItems'
    );
    const expenseItems = initializeSelectableItemsWithSubItems(
        InvoiceItemsTypeEnum.Expense,
        booking.expenses || [],
        'items'
    );
    return {
        bookingId: booking.bookingId,
        clientId: booking.clientId,
        invoiceNumber: booking.invoiceNumber,
        status: SelectionStatus.NOT_SELECTED,
        items: [...timesheetItems, ...expenseItems],
    };
};
const initializeState = ({ bookings, totalCount }) => ({
    selectStatus: SelectionStatus.NOT_SELECTED,
    totalCount,
    bookings: bookings?.map(initializeBooking),
});

const syncStoreSelectionStatus = (state) => {
    const allBookingsSelected = state.bookings.every(
        (booking) => booking.status === SelectionStatus.SELECTED
    );
    if (allBookingsSelected) {
        return { ...state, selectStatus: SelectionStatus.SELECTED };
    }

    const someBookingsSelected = state.bookings.some(
        (booking) =>
            booking.status === SelectionStatus.SELECTED ||
            booking.status === SelectionStatus.INDETERMINATE
    );
    return {
        ...state,
        selectStatus: someBookingsSelected
            ? SelectionStatus.INDETERMINATE
            : SelectionStatus.NOT_SELECTED,
    };
};

const syncBookingSelectionStatus = (booking) => {
    const bookingCopy = { ...booking };
    const allItemsSelected = bookingCopy.items.every(
        (item) => item.status === SelectionStatus.SELECTED
    );
    if (allItemsSelected) {
        bookingCopy.status = SelectionStatus.SELECTED;
    } else {
        const someItemsSelected = bookingCopy.items.some(
            (item) =>
                item.status === SelectionStatus.SELECTED ||
                item.status === SelectionStatus.INDETERMINATE
        );
        bookingCopy.status = someItemsSelected
            ? SelectionStatus.INDETERMINATE
            : SelectionStatus.NOT_SELECTED;
    }
    return bookingCopy;
};

const updateBookingItemSelectionStatus = (item, newStatus) => {
    if (item.hasSubItems) {
        return {
            ...item,
            status: newStatus,
            subItems: item.subItems.map((subItem) => ({ ...subItem, status: newStatus })),
        };
    }
    return { ...item, status: newStatus };
};

const setBookingAsSelected = (booking) => ({
    ...booking,
    status: SelectionStatus.SELECTED,
    items: booking.items.map((item) =>
        updateBookingItemSelectionStatus(item, SelectionStatus.SELECTED)
    ),
});

const setBookingAsNotSelected = (booking) => ({
    ...booking,
    status: SelectionStatus.NOT_SELECTED,
    items: booking.items.map((item) =>
        updateBookingItemSelectionStatus(item, SelectionStatus.NOT_SELECTED)
    ),
});

const toggleBookingStatus = (booking) =>
    booking.status !== SelectionStatus.SELECTED
        ? setBookingAsSelected(booking)
        : setBookingAsNotSelected(booking);

function reducer(state, action) {
    switch (action.type) {
        case 'initialize_new_bookings': {
            const newSelectStatus = [
                SelectionStatus.SELECTED,
                SelectionStatus.INDETERMINATE,
            ].includes(state.selectStatus)
                ? SelectionStatus.INDETERMINATE
                : SelectionStatus.NOT_SELECTED;
            return {
                ...state,
                selectStatus: newSelectStatus,
                bookings: [...state.bookings, ...action.payload.bookings.map(initializeBooking)],
            };
        }

        case 'toggle_all_bookings': {
            const newSelectStatus =
                state.selectStatus === SelectionStatus.SELECTED
                    ? SelectionStatus.NOT_SELECTED
                    : SelectionStatus.SELECTED;
            const mapFn =
                newSelectStatus === SelectionStatus.SELECTED
                    ? setBookingAsSelected
                    : setBookingAsNotSelected;
            return {
                ...state,
                selectStatus: newSelectStatus,
                bookings: state.bookings.map(mapFn),
            };
        }

        case 'toggle_select_booking_chunk': {
            const { bookingIds } = action.payload;

            const chunkStatuses = state.bookings
                .filter((booking) => bookingIds.includes(booking.bookingId))
                .map((booking) => booking.status);

            // if all bookings in the chunk are selected, set the chunk as not selected, otherwise set it as selected
            const mapFn = chunkStatuses.every((status) => status === SelectionStatus.SELECTED)
                ? setBookingAsNotSelected
                : setBookingAsSelected;

            return syncStoreSelectionStatus({
                ...state,
                bookings: state.bookings.map((booking) =>
                    bookingIds.includes(booking.bookingId) ? mapFn(booking) : booking
                ),
            });
        }

        case 'set_select_booking_chunk': {
            const { bookingIds, newStatus } = action.payload;

            const mapFn =
                newStatus === SelectionStatus.SELECTED
                    ? setBookingAsSelected
                    : setBookingAsNotSelected;

            return syncStoreSelectionStatus({
                ...state,
                bookings: state.bookings.map((booking) =>
                    bookingIds.includes(booking.bookingId) ? mapFn(booking) : booking
                ),
            });
        }

        case 'select_booking': {
            const { bookingId } = action.payload;
            const bookingIndex = state.bookings.findIndex((item) => item.bookingId === bookingId);
            if (bookingIndex === -1) return state;
            const newState = { ...state, bookings: [...state.bookings] };
            newState.bookings[bookingIndex] = toggleBookingStatus(state.bookings[bookingIndex]);
            return syncStoreSelectionStatus(newState);
        }

        case 'select_item': {
            const { bookingId, itemType, itemId } = action.payload;
            const bookingIndex = state.bookings.findIndex((item) => item.bookingId === bookingId);
            if (bookingIndex === -1) return state;
            const itemIndex = state.bookings[bookingIndex].items.findIndex(
                (item) => item.type === itemType && item.id === itemId
            );
            if (itemIndex === -1) return state;

            const newStatus =
                state.bookings[bookingIndex].items[itemIndex].status !== SelectionStatus.SELECTED
                    ? SelectionStatus.SELECTED
                    : SelectionStatus.NOT_SELECTED;
            const itemCopyWithNewStatus = updateBookingItemSelectionStatus(
                state.bookings[bookingIndex].items[itemIndex],
                newStatus
            );

            const bookingCopy = {
                ...state.bookings[bookingIndex],
                items: [...state.bookings[bookingIndex].items],
            };
            bookingCopy.items[itemIndex] = itemCopyWithNewStatus;

            const newState = { ...state, bookings: [...state.bookings] };
            newState.bookings[bookingIndex] = syncBookingSelectionStatus(bookingCopy); // Calculate new booking state after item selection change
            return syncStoreSelectionStatus(newState);
        }

        case 'set_select_item_chunk': {
            const { bookingId, itemRefs, newStatus } = action.payload; // itemRefs is an array of objects of type { type: string, id: number }
            const bookingIndex = state.bookings.findIndex((item) => item.bookingId === bookingId);
            if (bookingIndex === -1) return state;

            const generateItemKey = ({ type, id }) => `${type}:${id}`;
            const itemRefSet = new Set(itemRefs.map(generateItemKey)); // remove duplicate items
            const bookingCopy = {
                ...state.bookings[bookingIndex],
                items: state.bookings[bookingIndex].items.map((item) => {
                    const itemKey = generateItemKey(item);
                    if (!itemRefSet.has(itemKey)) return item;
                    return updateBookingItemSelectionStatus(item, newStatus); // if the item is in the set, update the item's status
                }),
            };

            const newState = { ...state, bookings: [...state.bookings] };
            newState.bookings[bookingIndex] = syncBookingSelectionStatus(bookingCopy);
            return syncStoreSelectionStatus(newState);
        }

        case 'update_sub_items_selection': {
            const { bookingId, itemType, itemId, subItemIds: newSubItemIds } = action.payload;
            const bookingIndex = state.bookings.findIndex((item) => item.bookingId === bookingId);
            if (bookingIndex === -1) return state;
            const itemIndex = state.bookings[bookingIndex].items.findIndex(
                (item) => item.type === itemType && item.id === itemId
            );
            if (itemIndex === -1) return state;
            const itemCopy = { ...state.bookings[bookingIndex].items[itemIndex] };
            itemCopy.subItems = itemCopy.subItems.map((subItem) => ({
                ...subItem,
                status: newSubItemIds.includes(subItem.id)
                    ? SelectionStatus.SELECTED
                    : SelectionStatus.NOT_SELECTED,
            }));
            if (newSubItemIds.length === itemCopy.subItems.length) {
                itemCopy.status = SelectionStatus.SELECTED;
            } else if (newSubItemIds.length === 0) {
                itemCopy.status = SelectionStatus.NOT_SELECTED;
            } else {
                itemCopy.status = SelectionStatus.INDETERMINATE;
            }

            const bookingCopy = {
                ...state.bookings[bookingIndex],
                items: [...state.bookings[bookingIndex].items],
            };
            bookingCopy.items[itemIndex] = itemCopy;

            const newState = { ...state, bookings: [...state.bookings] };
            newState.bookings[bookingIndex] = syncBookingSelectionStatus(bookingCopy); // Calculate new booking state after sub items selection change
            return syncStoreSelectionStatus(newState);
        }

        case 'deselect_all_items': {
            return {
                ...state,
                selectStatus: SelectionStatus.NOT_SELECTED,
                bookings: state.bookings.map(setBookingAsNotSelected),
            };
        }

        case 'reset': {
            return {
                selectStatus: SelectionStatus.NOT_SELECTED,
                totalCount: 0,
                bookings: [],
            };
        }

        case 'set_all_bookings': {
            return {
                selectStatus: SelectionStatus.NOT_SELECTED,
                totalCount: state.totalCount,
                bookings: action.payload.bookings.map(initializeBooking),
            };
        }

        default: {
            throw Error(`Unknown action: ${action.type}`);
        }
    }
}

/*  state is an object with the following structure:
    {
        selectStatus: SelectionStatus,
        bookings: [{ 
            bookingId: number, 
            status: SelectionStatus,
            items: [
                { type: string, id: number, status: SelectionStatus, subItems: [{ id: number, selected: boolean }] }, // has sub items
                { type: string, id: number, selected: boolean }, // no sub items
            ]
        }]
    }
*/
export const InvoicingSelectionContextProvider = ({ children, bookings, totalCount }) => {
    const [state, dispatch] = useReducer(reducer, { bookings, totalCount }, initializeState);

    const handleInitializeNewBookings = useCallback(
        (newBookings) => {
            const existingBookingsIds = state.bookings?.map((booking) => booking.bookingId);
            const missingBookings = newBookings?.filter(
                (booking) => !existingBookingsIds.includes(booking.bookingId)
            );
            if (missingBookings && missingBookings?.length > 0) {
                dispatch({
                    type: 'initialize_new_bookings',
                    payload: { bookings: missingBookings },
                });
            }
        },
        [state.bookings]
    );

    const handleSelectAllBookings = useCallback(() => {
        dispatch({ type: 'toggle_all_bookings' });
    }, []);

    const handleToggleSelectBookingChunk = useCallback((bookingIds) => {
        dispatch({ type: 'toggle_select_booking_chunk', payload: { bookingIds } });
    }, []);

    const handleSetSelectBookingChunk = useCallback((bookingIds, newStatus) => {
        dispatch({ type: 'set_select_booking_chunk', payload: { bookingIds, newStatus } });
    }, []);

    const handleSelectBooking = useCallback((bookingId) => {
        dispatch({ type: 'select_booking', payload: { bookingId } });
    }, []);

    const handleSelectItem = useCallback((bookingId, itemType, itemId) => {
        dispatch({ type: 'select_item', payload: { bookingId, itemType, itemId } });
    }, []);

    const handleSetSelectItemChunk = useCallback((bookingId, itemRefs, newStatus) => {
        dispatch({
            type: 'set_select_item_chunk',
            payload: { bookingId, itemRefs, newStatus },
        });
    }, []);

    const handleUpdateSubItemsSelection = useCallback((bookingId, itemType, itemId, subItemIds) => {
        dispatch({
            type: 'update_sub_items_selection',
            payload: { bookingId, itemType, itemId, subItemIds },
        });
    }, []);

    const deselectAllItems = useCallback(() => {
        dispatch({ type: 'deselect_all_items' });
    }, []);

    const handleReset = useCallback(() => {
        dispatch({ type: 'reset' });
    }, []);

    const handleSetAllBookings = useCallback((newBookings) => {
        dispatch({ type: 'set_all_bookings', payload: { bookings: newBookings ?? [] } });
    }, []);

    // count the number of top level items that are selected (bookings)
    const selectedBookingsCount = useMemo(
        () =>
            state.bookings.filter((booking) => booking.status === SelectionStatus.SELECTED).length,
        [state]
    );

    // count the total number of selected items (top level items and sub items)
    const selectedItemsCount = useMemo(
        () =>
            state?.bookings?.reduce((acc, booking) => {
                if (!booking?.items || booking?.items?.length === 0) {
                    return booking.status === SelectionStatus.SELECTED ? acc + 1 : acc;
                }
                let newSelectedItems = 0;
                booking.items.forEach((item) => {
                    if (item.hasSubItems) {
                        item.subItems.forEach((subItem) => {
                            if (subItem.status === SelectionStatus.SELECTED) {
                                newSelectedItems++;
                            }
                        });
                    } else if (item.status === SelectionStatus.SELECTED) {
                        newSelectedItems++;
                    }
                });
                return acc + newSelectedItems;
            }, 0),
        [state]
    );

    const getSelectedInvoices = useCallback(() => {
        if (!state?.bookings?.length) return [];

        return state.bookings.reduce((acc, booking) => {
            if (booking.status === SelectionStatus.SELECTED) acc.push(booking);
            return acc;
        }, []);
    }, [state.bookings]);

    const getSelectedItems = useCallback(() => {
        // Helper function to map item types to API format
        function getItemType(type, subItemType = null) {
            switch (type) {
                case InvoiceItemsTypeEnum.Timesheet:
                    return subItemType;
                case InvoiceItemsTypeEnum.Expense:
                    return 'Expense';
                default:
                    return '';
            }
        }

        if (!state?.bookings?.length) return [];

        return state.bookings.reduce((acc, booking) => {
            // Skip bookings without items or with no selected items
            if (!booking?.items || booking.items.length === 0) {
                return acc;
            }

            const includedItems = [];

            booking.items.forEach((item) => {
                if (item.hasSubItems) {
                    item.subItems.forEach((subItem) => {
                        if (subItem.status === SelectionStatus.SELECTED) {
                            includedItems.push({
                                itemType: getItemType(item.type, subItem.itemType),
                                itemId: subItem.id,
                                isDelta: subItem.isDelta,
                            });
                        }
                    });
                } else if (item.status === SelectionStatus.SELECTED) {
                    includedItems.push({
                        itemType: getItemType(item.type),
                        itemId: item.id,
                        isDelta: item.isDelta,
                    });
                }
            });

            if (includedItems.length > 0) {
                acc.push({
                    bookingId: booking?.bookingId,
                    clientId: booking?.clientId,
                    included: includedItems,
                });
            }

            return acc;
        }, []);
    }, [state.bookings]);

    const getBookingSelectionState = useCallback(
        (bookingId) => {
            if (state.selectStatus === SelectionStatus.SELECTED) return SelectionStatus.SELECTED;

            return (
                state?.bookings?.find((item) => item.bookingId === bookingId)?.status ||
                SelectionStatus.NOT_SELECTED
            );
        },
        [state]
    );

    const getBookingItemSelectionState = useCallback(
        (bookingId, itemType, itemId) => {
            if (state.selectStatus === SelectionStatus.SELECTED) return SelectionStatus.SELECTED;

            const booking = state?.bookings?.find((item) => item.bookingId === bookingId);
            if (!booking || booking.status === SelectionStatus.NOT_SELECTED)
                return SelectionStatus.NOT_SELECTED;
            if (booking.status === SelectionStatus.SELECTED) return SelectionStatus.SELECTED;
            return (
                booking.items.find((item) => item.type === itemType && item.id === itemId)
                    ?.status || SelectionStatus.NOT_SELECTED
            );
        },
        [state]
    );

    const getSelectedSubItems = useCallback(
        (bookingId, itemType, itemId) => {
            const booking = state?.bookings?.find((item) => item.bookingId === bookingId);
            if (!booking || booking.status === SelectionStatus.NOT_SELECTED) return [];
            const bookingItem = booking.items?.find(
                (item) => item.type === itemType && item.id === itemId
            );
            if (!bookingItem || !bookingItem.hasSubItems) return [];
            return (
                bookingItem.subItems
                    ?.filter((subItem) => subItem.status === SelectionStatus.SELECTED)
                    ?.map(({ id }) => id) || []
            );
        },
        [state]
    );

    useEffect(() => {
        handleInitializeNewBookings(bookings);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookings]);

    const memoizedContextValue = useMemo(
        () => ({
            selectStatus: state.selectStatus,
            selectedBookingsCount,
            selectedItemsCount,
            getSelectedItems,
            getSelectedInvoices,
            handleInitializeNewBookings,
            handleSelectAllBookings,
            handleToggleSelectBookingChunk,
            handleSetSelectBookingChunk,
            handleSelectBooking,
            handleSelectItem,
            handleSetSelectItemChunk,
            handleUpdateSubItemsSelection,
            getBookingSelectionState,
            getBookingItemSelectionState,
            getSelectedSubItems,
            deselectAllItems,
            handleReset,
            handleSetAllBookings,
        }),
        [
            state.selectStatus,
            selectedBookingsCount,
            selectedItemsCount,
            getSelectedItems,
            getSelectedInvoices,
            handleInitializeNewBookings,
            handleSelectAllBookings,
            handleToggleSelectBookingChunk,
            handleSetSelectBookingChunk,
            handleSelectBooking,
            handleSelectItem,
            handleSetSelectItemChunk,
            handleUpdateSubItemsSelection,
            getBookingSelectionState,
            getBookingItemSelectionState,
            getSelectedSubItems,
            deselectAllItems,
            handleReset,
            handleSetAllBookings,
        ]
    );

    return (
        <InvoicingSelectionContext.Provider value={memoizedContextValue}>
            {typeof children === 'function' ? children(memoizedContextValue) : children}
        </InvoicingSelectionContext.Provider>
    );
};
